import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { LegalPageLayout } from "@/components/LegalPageLayout";
import { SchemaOrg, getBreadcrumbSchema, getOrganizationSchema, getWebSiteSchema } from "@/components/seo/SchemaOrg";
import { HreflangTags } from "@/components/seo/HreflangTags";
import { getLocalizedUrl } from "@/hooks/useSeoMeta";
import { LocaleKey } from "@/i18n";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/contexts/LocaleContext";

export default function PrivacyPolicy() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  
  const baseUrl = getLocalizedUrl(locale as LocaleKey, '/');

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: t('nav.home', 'Home'), url: baseUrl },
    { name: t('privacy.breadcrumb'), url: getLocalizedUrl(locale as LocaleKey, '/politica-de-privacidad') }
  ]);

  const langCode = locale === 'pt-BR' ? 'pt-BR' : locale === 'en' || locale === 'en-EU' ? 'en' : 'es';

  const organizationSchema = getOrganizationSchema(locale as LocaleKey);
  const webSiteSchema = getWebSiteSchema(locale as LocaleKey);

  const privacySchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": t('privacy.pageTitle'),
    "description": t('privacy.pageDescription'),
    "url": getLocalizedUrl(locale as LocaleKey, '/politica-de-privacidad'),
    "inLanguage": langCode,
    "isPartOf": {
      "@type": "WebSite",
      "name": "Fora da Rota",
      "url": baseUrl
    },
    "about": {
      "@type": "Thing",
      "name": t('privacy.breadcrumb')
    },
    "lastReviewed": "2025-03-31"
  };

  return (
    <div className="min-h-screen bg-background">
      <HreflangTags path="/politica-de-privacidad" />
<SchemaOrg schema={[organizationSchema, webSiteSchema, breadcrumbSchema, privacySchema]} />
      
      <Navbar />
      
      <main id="main-content">
        <LegalPageLayout 
          title={t('privacy.title')} 
          lastUpdate={t('privacy.lastUpdate')}
        >
          <div className="space-y-4 mb-10">
            <p className="text-muted-foreground leading-relaxed">{t('privacy.intro1')}</p>
            <p className="text-muted-foreground leading-relaxed">{t('privacy.intro2')}</p>
            <p className="text-muted-foreground leading-relaxed">{t('privacy.intro3')}</p>
          </div>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('privacy.section1Title')}</h2>
            <p className="text-muted-foreground mb-6">{t('privacy.section1Intro')}</p>
            
            <h3 className="text-lg font-medium text-foreground mb-3">{t('privacy.section1_1Title')}</h3>
            <ul className="text-muted-foreground space-y-2 list-disc pl-6 mb-6">
              <li>{t('privacy.section1_1_1')}</li>
            </ul>

            <h3 className="text-lg font-medium text-foreground mb-3">{t('privacy.section1_2Title')}</h3>
            <ul className="text-muted-foreground space-y-2 list-disc pl-6 mb-6">
              <li>{t('privacy.section1_2_1')}</li>
              <li>{t('privacy.section1_2_2')}</li>
            </ul>

            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <p className="text-muted-foreground text-sm">{t('privacy.section1Note1')}</p>
              <p className="text-muted-foreground text-sm">{t('privacy.section1Note2')}</p>
            </div>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('privacy.section2Title')}</h2>
            <p className="text-muted-foreground mb-4">{t('privacy.section2Intro')}</p>
            <ul className="text-muted-foreground space-y-3 list-disc pl-6">
              <li>{t('privacy.section2_1')}</li>
              <li>{t('privacy.section2_2')}</li>
              <li>{t('privacy.section2_3')}</li>
              <li>{t('privacy.section2_4')}</li>
              <li>{t('privacy.section2_5')}</li>
            </ul>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('privacy.section3Title')}</h2>
            <p className="text-muted-foreground mb-4">{t('privacy.section3Intro')}</p>
            <ul className="text-muted-foreground space-y-3 list-disc pl-6">
              <li>{t('privacy.section3_1')}</li>
              <li>{t('privacy.section3_2')}</li>
              <li>{t('privacy.section3_3')}</li>
            </ul>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('privacy.section4Title')}</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>{t('privacy.section4_1')}</p>
              <p>{t('privacy.section4_2')}</p>
            </div>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('privacy.section5Title')}</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>{t('privacy.section5_1')}</p>
              <p>{t('privacy.section5_2')}</p>
            </div>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('privacy.section6Title')}</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>{t('privacy.section6_1')}</p>
              <p>{t('privacy.section6_2')}</p>
            </div>
          </section>

          <section className="mb-10 pb-10 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('privacy.section7Title')}</h2>
            <p className="text-muted-foreground">{t('privacy.section7_1')}</p>
          </section>

          <section className="mb-10">
            <h2 className="text-xl font-semibold text-foreground mb-4">{t('privacy.section8Title')}</h2>
            <p className="text-muted-foreground mb-4">{t('privacy.section8Intro')}</p>
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4">
              <a 
                href="mailto:contato@foradarota.com" 
                className="text-primary hover:underline font-medium"
              >
                {t('privacy.section8Email')}
              </a>
            </div>
            <p className="text-muted-foreground">{t('privacy.section8Note')}</p>
          </section>

          <div className="bg-muted/50 rounded-lg p-6">
            <p className="text-muted-foreground text-sm">{t('privacy.footer')}</p>
          </div>
        </LegalPageLayout>
      </main>
      
      <Footer />
    </div>
  );
}